import type { WalletStatus } from '@services/wallet.service';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const INR_FMT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

function fmtINR(n: number): string {
  return INR_FMT.format(Math.max(0, n));
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return '';
  }
}

export interface WalletBannerProps {
  status: WalletStatus;
}

/**
 * Top banner for the non-blocking wallet warning states:
 *
 * - `suspend_warning` — balance can't cover the upcoming renewal and the
 *   renewal is < 7 days out. Tells the user the exact date service will be
 *   suspended (the day before renewal) so they can recharge in time.
 * - `low` — balance is at/below ₹50 but renewal is not imminent. A softer
 *   "top up soon" nudge.
 *
 * Renders nothing for `ok` / `blocked` (the blocker overlay owns `blocked`).
 * The app stays fully usable underneath.
 */
export function WalletBanner({ status }: WalletBannerProps): JSX.Element | null {
  if (status.state === 'suspend_warning') {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-4 border-b border-amber-200 bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] px-6 py-2.5 text-[13px] text-amber-900"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} strokeWidth={2} className="shrink-0 text-amber-600" />
          <span>
            Balance is <span className="font-semibold">{fmtINR(status.balance)}</span> — too
            low for the next renewal.{' '}
            {status.suspend_date ? (
              <>
                Service will be suspended on{' '}
                <span className="font-semibold">{fmtDate(status.suspend_date)}</span>.
              </>
            ) : (
              <>Recharge to avoid suspension.</>
            )}
          </span>
        </div>
        <Link
          to="/billing"
          className="shrink-0 font-semibold text-amber-900 underline-offset-2 hover:underline"
        >
          Recharge &rarr;
        </Link>
      </div>
    );
  }

  if (status.state === 'low') {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-4 border-b border-amber-200 bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] px-6 py-2.5 text-[13px] text-amber-900"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} strokeWidth={2} className="shrink-0 text-amber-600" />
          <span>
            Wallet balance is low —{' '}
            <span className="font-semibold">{fmtINR(status.balance)}</span>. Top up to keep
            messaging uninterrupted.
          </span>
        </div>
        <Link
          to="/billing"
          className="shrink-0 font-semibold text-amber-900 underline-offset-2 hover:underline"
        >
          Recharge &rarr;
        </Link>
      </div>
    );
  }

  return null;
}

export default WalletBanner;
